import { type ComputedRef, type Ref, computed } from 'vue'

import { ASSISTANT_FAB_CLEARANCE_OFFSET_PX } from '../constants'

import { useIsShoppingAssistantFabVisible } from './useIsShoppingAssistantFabVisible'
import { useShoppingAssistantSqueezeOffset } from './useShoppingAssistantSqueezeOffset'

export interface ShoppingAssistantPopupOffset {
  /** Extra bottom clearance (px) to clear the FAB's footprint. 0 unless the FAB is visible. */
  bottomOffsetPx: ComputedRef<number>
  /** Extra right inset (px) to clear the pinned side panel. 0 unless it's squeezing the layout. */
  rightOffsetPx: ComputedRef<number>
  /** `bottomOffsetPx`/`rightOffsetPx` as an inline style, applied only while `isDesktop`. */
  offsetStyle: ComputedRef<{ marginBottom?: string; marginRight?: string }>
}

/**
 * Offsets that unrelated bottom-right popups (the TelcoPartner PDP popups,
 * the lead-collection popup) add on top of their own bottom/right position
 * so they never render on top of the shopping-assistant FAB or its pinned
 * side panel. Meant to be applied additively (e.g. via margin) so consumers
 * don't need to duplicate their own base bottom/right pixel values.
 *
 * `isDesktop` decides when the offset applies at all — on mobile these
 * popups are full-width sheets where it doesn't — and is left to the caller
 * since each popup can size its own breakpoint differently.
 */
export function useShoppingAssistantPopupOffset(
  isDesktop: Ref<boolean> | ComputedRef<boolean>,
): ShoppingAssistantPopupOffset {
  const isFabVisible = useIsShoppingAssistantFabVisible()
  const { squeezeOffsetPx } = useShoppingAssistantSqueezeOffset()

  const bottomOffsetPx = computed(() =>
    isFabVisible.value ? ASSISTANT_FAB_CLEARANCE_OFFSET_PX : 0,
  )
  const rightOffsetPx = computed(() => squeezeOffsetPx.value)

  const offsetStyle = computed(() => ({
    marginBottom:
      isDesktop.value && bottomOffsetPx.value
        ? `${bottomOffsetPx.value}px`
        : undefined,
    marginRight:
      isDesktop.value && rightOffsetPx.value
        ? `${rightOffsetPx.value}px`
        : undefined,
  }))

  return { bottomOffsetPx, rightOffsetPx, offsetStyle }
}
